import { useState } from "react";
import { Button, Card, CardContent, cn, Skeleton, Badge } from "@tellescope/ui";
import { formatDate } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { FileText, CheckCircle2, Clock, Edit3, ChevronRight } from "lucide-react";
import type { FormResponse } from "@tellescope/types-models";
import type { FormResponseListBlock as FormResponseListBlockType, FormResponseCategory } from "../../../shared/schema";
import { MobileList } from "../MobileList";
import { useEnduserFormResponses } from "../../hooks/useEnduserFormResponses";
import { useFormResponsesFilter } from "../../hooks/useItemFilter";
import { FormResponseDialog } from "./FormResponseDialog";
import { invalidateFormResponseQueries } from "../enduserQueryClient";
import { i18n } from "@tellescope/i18n";

interface FormResponseListBlockProps {
  block: FormResponseListBlockType;
}

const getDisplayTitle = (formTitle: string, titleOverrides?: Record<string, string>): string => {
  return titleOverrides?.[formTitle] ?? formTitle;
};

interface FormResponseCardProps {
  formResponse: FormResponse;
  titleOverrides?: Record<string, string>;
  onClick: () => void;
}

function FormResponseCard({ formResponse, titleOverrides, onClick }: FormResponseCardProps) {
  const { t } = useTranslation("forms");
  const isSubmitted = !!formResponse.submittedAt;
  const hasDraft = !!formResponse.draftSavedAt;

  return (
    <Card className="overflow-hidden cursor-pointer hover:bg-accent/50 transition-colors" onClick={onClick}>
      <CardContent className="px-4">
        <div className="flex gap-4">
          {/* Form icon */}
          <div className="flex-shrink-0">
            <div
              className={cn(
                "w-14 h-14 rounded-lg flex flex-col items-center justify-center",
                isSubmitted ? "bg-green-100 dark:bg-green-900/30" : "bg-amber-100 dark:bg-amber-900/30"
              )}
            >
              {isSubmitted ? (
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              ) : (
                <Edit3 className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              )}
            </div>
          </div>

          {/* Form details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-sm truncate">
                {getDisplayTitle(formResponse.formTitle, titleOverrides)}
              </h4>
              <Badge variant={isSubmitted ? "secondary" : "outline"} className="flex-shrink-0 text-xs">
                {isSubmitted ? t("Submitted") : hasDraft ? t("Draft") : t("Pending")}
              </Badge>
            </div>

            {isSubmitted && formResponse.submittedAt && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <Clock className="h-3 w-3" />
                <span>{t("Submitted {{timestamp}}", { timestamp: formatDate(formResponse.submittedAt) })}</span>
              </div>
            )}

            {!isSubmitted && hasDraft && formResponse.draftSavedAt && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <Clock className="h-3 w-3" />
                <span>{t("Draft saved {{timestamp}}", { timestamp: formatDate(formResponse.draftSavedAt) })}</span>
              </div>
            )}

            {!isSubmitted && !hasDraft && (
              <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-1">
                <FileText className="h-3 w-3" />
                <span>{t("Needs completion")}</span>
              </div>
            )}

            {/* Show score summary if available */}
            {formResponse.scores && formResponse.scores.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formResponse.scores.slice(0, 2).map((score, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {score.title}: {score.value}
                  </Badge>
                ))}
                {formResponse.scores.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{formResponse.scores.length - 2}
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Chevron indicator */}
          <div className="flex-shrink-0 self-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FormResponseListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="px-4 py-3">
            <div className="flex gap-4">
              <Skeleton className="w-14 h-14 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Category labels for display
const categoryLabels: Record<FormResponseCategory, string> = {
  submitted: i18n.t("Submitted", { ns: "forms" }),
  unsubmitted: i18n.t("Unsubmitted", { ns: "forms" }),
  all: i18n.t("All", { ns: "forms" }),
};

interface CategorySelectorProps {
  categories: FormResponseCategory[];
  selectedCategory: FormResponseCategory;
  onSelectCategory: (category: FormResponseCategory) => void;
}

function CategorySelector({ categories, selectedCategory, onSelectCategory }: CategorySelectorProps) {
  return (
    <div className="flex gap-1 p-1 bg-muted rounded-lg">
      {categories.map((category) => (
        <Button
          key={category}
          variant="ghost"
          size="sm"
          className={cn(
            "flex-1 h-8 text-xs font-medium",
            selectedCategory === category
              ? "bg-background text-foreground shadow-sm hover:bg-background"
              : "text-muted-foreground hover:bg-transparent hover:text-foreground"
          )}
          onClick={() => onSelectCategory(category)}
        >
          {categoryLabels[category]}
        </Button>
      ))}
    </div>
  );
}

export function FormResponseListBlock({ block }: FormResponseListBlockProps) {
  const { t } = useTranslation("forms");
  const categories = block.props.categories ?? ["all"];
  const { itemFilter, titleOverrides } = block.props;
  const showCategorySelector = categories.length > 1;

  // Initialize with the first category in the list
  const [selectedCategory, setSelectedCategory] = useState<FormResponseCategory>(categories[0] ?? "all");
  const [selectedFormResponse, setSelectedFormResponse] = useState<FormResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    formResponses: rawFormResponses,
    isLoading: responsesLoading,
    error,
  } = useEnduserFormResponses({
    category: selectedCategory,
  });

  // Apply item filter if configured
  const { filteredItems: formResponses, isLoading: filterLoading } = useFormResponsesFilter({
    items: rawFormResponses,
    itemFilter,
  });

  const isLoading = responsesLoading || filterLoading;

  const handleFormResponseClick = (formResponse: FormResponse) => {
    setSelectedFormResponse(formResponse);
    setDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Invalidate form response queries to refresh after potential submission
      invalidateFormResponseQueries();

      // Clear selection after dialog closes (small delay for animation)
      setTimeout(() => setSelectedFormResponse(null), 200);
    }
  };

  // Determine title for "view more" modal based on selected category
  const getModalTitle = () => {
    switch (selectedCategory) {
      case "submitted":
        return t("Submitted Forms");
      case "unsubmitted":
        return t("Pending Forms");
      case "all":
      default:
        return t("All Forms");
    }
  };

  // Determine empty message based on selected category
  const getDefaultEmptyMessage = () => {
    switch (selectedCategory) {
      case "submitted":
        return t("No submitted forms");
      case "unsubmitted":
        return t("No pending forms");
      case "all":
      default:
        return t("No forms");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {showCategorySelector && (
          <CategorySelector
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        )}
        <FormResponseListSkeleton count={block.props.maxItems ?? 5} />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">{t("Failed to load forms")}</CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {showCategorySelector && (
          <CategorySelector
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        )}
        <MobileList
          items={formResponses}
          renderItem={(formResponse) => (
            <FormResponseCard
              formResponse={formResponse}
              titleOverrides={titleOverrides}
              onClick={() => handleFormResponseClick(formResponse)}
            />
          )}
          title={getModalTitle()}
          maxItems={block.props.maxItems ?? 5}
          emptyMessage={block.props.emptyMessage ?? getDefaultEmptyMessage()}
        />
      </div>

      <FormResponseDialog
        formResponse={selectedFormResponse}
        titleOverride={selectedFormResponse ? titleOverrides?.[selectedFormResponse.formTitle] : undefined}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
      />
    </>
  );
}
